// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { tableProps } from "@store/Themes/common/props/table";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import { BackOrdersDataViewContext } from "@store/Framework/Store/Store/Data/BackOrders/BackOrdersSelectors";
import updateSearchFields from "@store/Framework/Store/Store/Pages/BackOrders/Handlers/UpdateSearchFields";
import BackOrdersModals from "@store/Library/Components/BackOrders/BackOrdersModals";
import BackOrdersRow from "@store/Library/Components/BackOrders/BackOrdersRow";
import constants from "@store/constants";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const ResultCount = getWidgetComponent("BackOrders/BackOrdersResultCount");
const BackOrdersPagination = getWidgetComponent("BackOrders/BackOrdersPagination");

const enum fields {
    displayActionButtons = "displayActionButtons",
    displayEditButton = "displayEditButton",
    destination = "destination",
    overrideTitle = "overrideTitle",
}

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    ProductTitle: ["ProductTitle"],
    OriginalOrderDate: ["OriginalOrderDate"],
    NextAvailability: ["ProductNextAvailabilityDate"],
    BackOrderQuantity: ["BackOrderQuantity"],
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.displayActionButtons]: boolean;
        [fields.displayEditButton]: boolean;
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        parameter: state.store.pages.backOrders.getBackOrdersParameter,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface BackOrdersListStyles {
    container?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    actionsHeader?: DataTableHeaderProps;
    dataTable?: DataTableProps;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    headingWrapper?: FlexWrapperPresentationProps;
}

export const backOrdersListStyles: BackOrdersListStyles = {
    loadingOverlay: tableProps.loadingOverlay,
    tableWrapper: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    headingWrapper: tableProps.headingWrapper,
    noResultsText: typographyGetNoResultsAltProps({ spaced: true }),
    dataTable: tableProps.dataTable,
    orderTotalHeader: tableProps.amountHeader,
    actionsHeader: tableProps.tightHeader,
};

const styles = backOrdersListStyles;

export const BackOrdersList: FC<Props> = ({
    parameter,
    updateSearchFields,
    fields: { displayActionButtons, displayEditButton, destination, overrideTitle },
}) => {
    const backOrdersDataView = usePersistentDataView(useContext(BackOrdersDataViewContext));
    const link = useGetLink(destination);

    const headerClick = (sortField: string) => {
        const previousSort = parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const fields = sortByFields[sortField];
        const sort =
            Array.isArray(fields) && fields.length > 0
                ? fields.map(field => `${field}${direction}`).join(", ")
                : `${sortField}${direction}`;
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!parameter.sort) {
            return sorted;
        }
        const [primaryField] = parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    return (
        <DataViewLoadingOverlay dataView={backOrdersDataView} {...styles.loadingOverlay}>
            {!backOrdersDataView.value?.length ? (
                <StyledWrapper>
                    <Typography {...styles.noResultsText} data-test-selector="backOrdersList_noOrdersFound">
                        {translate("No orders found")}
                    </Typography>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container} data-test-selector="backOrdersList">
                    <FlexWrapper {...styles.headingWrapper}>
                        <ResultCount />
                    </FlexWrapper>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                <DataTableHeader {...styles.orderNumberHeader} />
                                <DataTableHeader
                                    {...styles.orderNumberHeader}
                                    title={translate("Description")}
                                    sorted={sorted("ProductTitle")}
                                    onSortClick={() => headerClick("ProductTitle")}
                                >
                                    {translate("Description")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.orderDateHeader}
                                    sorted={sorted("OriginalOrderDate")}
                                    onSortClick={() => headerClick("OriginalOrderDate")}
                                >
                                    {translate("Order Date")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.orderDateHeader}>
                                    {translate("Expected Date")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.orderTotalHeader}
                                    sorted={sorted("BackOrderQuantity")}
                                    onSortClick={() => headerClick("BackOrderQuantity")}
                                >
                                    {translate("Qty")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.actionsHeader}>
                                    <VisuallyHidden>{translate("Actions")}</VisuallyHidden>
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody data-test-selector="backOrdersList_tableBody">
                                {backOrdersDataView &&
                                    backOrdersDataView.value.map(backOrderItem => (
                                        <BackOrdersRow
                                            key={backOrderItem.id}
                                            backOrderItem={backOrderItem}
                                            showActions={displayActionButtons}
                                            showEditAction={displayEditButton}
                                            url={link.url}
                                            buttonText={overrideTitle || link.title}
                                        />
                                    ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    <BackOrdersPagination />
                </StyledWrapper>
            )}
            <BackOrdersModals />
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BackOrdersList),
    definition: {
        group: "Order History",
        displayName: "Back Orders List",
        allowedContexts: [constants.pageType.backOrdersPage],
        fieldDefinitions: [
            {
                name: fields.displayActionButtons,
                displayName: "Display Actions Buttons",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
            {
                name: fields.displayEditButton,
                displayName: "Display Edit Button",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: x => x.fields.displayActionButtons === true,
            },
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                isVisible: x => x.fields.displayActionButtons === false,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: x => x.fields.displayActionButtons === false,
            },
        ],
    },
};

export default widgetModule;
